import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const CreateGroup = () => {
  const { createGroup, uploadGroupLogo, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    isPublic: false,
    isSpecial: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const result = await createGroup(formData.title, formData.isPublic, formData.isSpecial);
    
    if (result.success) {
      let successMessage = t('groups.groupCreated') || 'Group created successfully!';
      
      // If special group and logo file provided, upload logo
      if (formData.isSpecial && logoFile) {
        const logoResult = await uploadGroupLogo(result.group.id, logoFile);
        if (logoResult.success) {
          successMessage += ' ' + (t('groups.logoUploaded') || 'Logo uploaded successfully!');
        } else {
          successMessage += ' ' + (t('groups.logoUploadFailed') || 'But logo upload failed: ') + logoResult.error;
        }
      }
      
      setSuccess(successMessage);
      setFormData({ title: '', isPublic: false, isSpecial: false });
      setLogoFile(null);
      
      // Navigate back to groups page after success
      setTimeout(() => {
        navigate('/groups');
      }, 2000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleFileChange = (e) => {
    setLogoFile(e.target.files[0]);
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>{t('groups.createNewGroup') || 'Create New Group'}</h2>
        
        {loading && <div className="loading">{t('common.loading') || 'Loading...'}</div>}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <form onSubmit={handleCreateGroup}>
            <div className="form-group">
              <label htmlFor="title">{t('groups.groupTitle') || 'Group Name'}:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder={t('groups.groupTitlePlaceholder') || 'Enter group name'}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="isPublic">{t('groups.groupType') || 'Group Type'}:</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    id="isPublic"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {t('groups.makePublic') || 'Make this group public (others can discover and join)'}
                </label>
                <small className="form-hint">
                  {formData.isPublic 
                    ? (t('groups.publicHint') || 'Public groups can be discovered and joined by anyone')
                    : (t('groups.privateHint') || 'Private groups require an invitation code to join')
                  }
                </small>
              </div>
            </div>
            
            {user?.isAdmin && (
              <div className="form-group">
                <label htmlFor="isSpecial">{t('groups.specialGroupType') || 'Special Group Type'}:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label special-group-label">
                    <input
                      type="checkbox"
                      id="isSpecial"
                      name="isSpecial"
                      checked={formData.isSpecial}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    {t('groups.makeSpecial') || 'Make this a special group (events can have logos and special styling)'}
                  </label>
                  <small className="form-hint special-hint">
                    {t('groups.specialHint') || 'Special groups allow events to have logo uploads and special dark green styling. Only admins can create special groups.'}
                  </small>
                </div>
              </div>
            )}

            {/* Group Logo Upload for Special Groups */}
            {user?.isAdmin && formData.isSpecial && (
              <div className="form-group">
                <label htmlFor="groupLogo">{t('groups.groupLogo') || 'Group Logo (Optional)'}:</label>
                <input
                  type="file"
                  id="groupLogo"
                  accept="image/*"
                  onChange={handleFileChange}
                  disabled={loading}
                  className="form-control"
                />
                {logoFile && (
                  <div className="selected-file" style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                    {t('groups.selectedFile') || 'Selected file'}: {logoFile.name}
                  </div>
                )}
                <small className="form-hint">
                  {t('groups.logoHint') || 'Upload a logo for your special group. Supported formats: JPG, PNG, GIF, SVG. Max size: 2MB.'}
                </small>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (t('common.creating') || 'Creating...') : (t('groups.createGroup') || 'Create Group')}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default CreateGroup; 